// src/pages/time/LeavePolicyPage.jsx
// Extracted from App.js - Leave Policy admin page
// BRIO Migration: Using code "5a" (table dense + striped)
import React from 'react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioFrameHeader,
  BrioByCode,
  BrioTable,
  getTablePropsFromCode
} from '../../components/BrioComponents';

// Presentation code for this page
const PRESENTATION_CODE = '5a';

// Role list used by both tables (single source of truth)
const ROLE_LEVELS = [
  'Intern', 'Junior Architect', 'Medior Architect', 'Project Architect',
  'Senior Architect', 'Associate', 'Partner', 'Director', 'Support',
  'Model Maker', 'Visualiser', 'Others'
];

export default function LeavePolicyPage({
  // Data - Leave
  selectedLeaveYear,
  leavePolicySaving,
  leavePolicyDirty,
  // State setters
  setSelectedLeaveYear,
  // Functions - Leave
  handleSaveLeavePolicy,
  getLeavePolicy,
  updateLeavePolicyMatrix,
  safeNumericInputValue,
  // Data - Notice
  noticePolicySaving,
  noticePolicyDirty,
  // Functions - Notice
  handleSaveNoticePolicy,
  getNoticePolicy,
  updateNoticePolicyMatrix,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // Combined dirty/saving state for the page-level save button
  const anyDirty = leavePolicyDirty || noticePolicyDirty;
  const anySaving = leavePolicySaving || noticePolicySaving;

  // Save both policies in sequence (only saves the dirty ones)
  const handleSaveAll = async () => {
    if (leavePolicyDirty) await handleSaveLeavePolicy();
    if (noticePolicyDirty) await handleSaveNoticePolicy();
  };

  return (
    <div className="brio-analytics-font min-h-screen" style={{ background: 'var(--brio-white)' }}>
      {/* PAGE HEADER BAND: Full-width white background */}
      <div style={{ background: 'var(--brio-white)', borderBottom: '1px solid var(--brio-border)' }}>
        <BrioFrame>
          <BrioFrameHeader
            title="Leave Policy Matrix"
            subtitle="Configure leave entitlements and notice periods by role level"
            right={
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="brio-text-m brio-font-medium">Year:</label>
                  <select 
                    value={selectedLeaveYear} 
                    onChange={(e) => setSelectedLeaveYear(parseInt(e.target.value))}
                    className="brio-btn brio-btn-secondary"
                  >
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                {/* Save button - saves both leave and notice policy */}
                <button
                  onClick={handleSaveAll}
                  disabled={anySaving || !anyDirty}
                  className={`brio-btn ${anyDirty ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  style={anySaving ? { opacity: 0.5, cursor: 'wait' } : !anyDirty ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  {anySaving ? 'Saving...' : anyDirty ? 'Save Changes' : 'Saved'}
                </button>
              </div>
            }
          />
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="py-4 space-y-4">
          {/* Policy Info Box */}
          <BrioAnalyticsSurface padding="m" style={{ background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)' }}>
            <div className="brio-analytics-muted" style={{ fontSize: '12px', fontWeight: 200 }}>
              <div className="mb-3">
                <span style={{ fontWeight: 400 }}>Leave Calculation Formula:</span>
                <span className="ml-2">Total Leave = Base Leave + Role Additional Leave + (Years Employed × Tenure Increment)</span>
              </div>

              <div>
                <span style={{ fontWeight: 400 }}>Leave Policies:</span>
                <ul className="mt-1 ml-4 space-y-0.5">
                  <li><span style={{ fontWeight: 400 }}>Leave Year:</span> Runs from 1 January to 31 December (calendar year). All annual entitlements and caps (entitlement, Emergency Leave 5-day cap, free 5 sick / WFH days) reset on 1 January.</li>
                  <li><span style={{ fontWeight: 400 }}>Maximum Cap:</span> Total annual leave entitlement is capped at 28 days (rounded up)</li>
                  <li><span style={{ fontWeight: 400 }}>Vacation:</span> Always counts against annual leave entitlement. Notice required: 5 days for 1–2 working days, 7 days for 3–5, 21 days for 6+. Maximum 15 working days per request, with at least 5 working days between consecutive vacation periods.</li>
                  <li><span style={{ fontWeight: 400 }}>Emergency Leave:</span> Always counts against annual leave entitlement. Can be requested only within 5 days of the start date and before 08:00 IST on the day the leave starts. Maximum 2 working days per request and 5 working days per leave year. Past the yearly cap, requests still go through and follow the normal route — supervisor approval first, then admin sign-off — but are flagged as over the cap.</li>
                  <li><span style={{ fontWeight: 400 }}>Sick Leave:</span> Deductible beyond 5 sick days per year. No advance notice required. Sick leave is intended for health-related absences. For short-notice personal matters, please use Emergency Leave.</li>
                  <li><span style={{ fontWeight: 400 }}>Work from Home:</span> Deductible beyond 5 WFH days per year. No advance notice required.</li>
                </ul>
              </div>
            </div>
          </BrioAnalyticsSurface>

          {/* Policy Matrix Table */}
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th className="text-left">Role Level</th>
                    <th className="text-center">Base Leave (days)</th>
                    <th className="text-center">Role Additional (days)</th>
                    <th className="text-center">Tenure Increment (days/year)</th>
                    <th className="text-center">Example: 5 years</th>
                  </tr>
                </thead>
                <tbody>
                  {ROLE_LEVELS.map(role => {
                    const policy = getLeavePolicy(role, selectedLeaveYear);
                    const example5yr = (safeNumericInputValue(policy.baseLeave) || 0) +
                                       (safeNumericInputValue(policy.roleAdditionalLeave) || 0) +
                                       (5 * (safeNumericInputValue(policy.tenureIncrementPerYear) || 0));
                    return (
                      <tr key={role}>
                        <td className="brio-font-medium">{role}</td>
                        <td className="text-center">
                          <input
                            type="number"
                            step="0.5"
                            value={safeNumericInputValue(policy.baseLeave)}
                            onChange={(e) => updateLeavePolicyMatrix(role, 'baseLeave', e.target.value, selectedLeaveYear)}
                            className="brio-modal-input"
                            style={{ width: '80px', textAlign: 'center', padding: '4px 8px' }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="number"
                            step="0.5"
                            value={safeNumericInputValue(policy.roleAdditionalLeave)}
                            onChange={(e) => updateLeavePolicyMatrix(role, 'roleAdditionalLeave', e.target.value, selectedLeaveYear)}
                            className="brio-modal-input"
                            style={{ width: '80px', textAlign: 'center', padding: '4px 8px' }}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="number"
                            step="0.1"
                            value={safeNumericInputValue(policy.tenureIncrementPerYear)}
                            onChange={(e) => updateLeavePolicyMatrix(role, 'tenureIncrementPerYear', e.target.value, selectedLeaveYear)}
                            className="brio-modal-input"
                            style={{ width: '80px', textAlign: 'center', padding: '4px 8px' }}
                          />
                        </td>
                        <td className="text-center">
                          {(() => {
                            const raw = example5yr;
                            const rounded = Math.ceil(raw);
                            const capped = Math.min(rounded, 28);
                            return (
                              <span className="brio-font-medium">
                                {capped} days
                                {capped === 28 && raw > 28 && (
                                  <span className="text-xs ml-1" style={{ color: 'var(--brio-orange)' }}>(capped)</span>
                                )}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>
          </BrioAnalyticsSurface>

          {/* ================================================================
              NOTICE POLICY SECTION (mirrors Leave Policy — 3-component model)
              notice_total_months = min(baseNotice + roleAdditionalNotice + (years × tenureIncrementPerYear), 6)
              All values in MONTHS
              ================================================================ */}

          {/* Notice Policy Info Box — same styling as Leave Policy info box */}
          <BrioAnalyticsSurface padding="m" style={{ background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)' }}>
            <div className="brio-analytics-muted" style={{ fontSize: '12px', fontWeight: 200 }}>
              <div className="mb-3">
                <span style={{ fontWeight: 400 }}>Notice Period Formula:</span>
                <span className="ml-2">Total Notice = Base Notice + Role Additional Notice + (Years Employed × Tenure Increment)</span>
              </div>
              
              <div>
                <span style={{ fontWeight: 400 }}>Notice Policies:</span>
                <ul className="mt-1 ml-4 space-y-0.5">
                  <li><span style={{ fontWeight: 400 }}>Unit:</span> Notice period is expressed in months</li>
                  <li><span style={{ fontWeight: 400 }}>Maximum Cap:</span> Total notice period is capped at 6 months (rounded up)</li>
                  <li><span style={{ fontWeight: 400 }}>Scope:</span> Applies to both employee resignation and employer termination unless otherwise specified in the employment contract</li>
                </ul>
              </div>
            </div>
          </BrioAnalyticsSurface>

          {/* Notice Policy Matrix Table — identical structure to Leave Policy table */}
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th className="text-left">Role Level</th>
                    <th className="text-center">Base Notice (months)</th>
                    <th className="text-center">Role Additional (months)</th>
                    <th className="text-center">Tenure Increment (months/year)</th>
                    <th className="text-center">Example: 5 years</th>
                  </tr>
                </thead>
                <tbody>
                  {ROLE_LEVELS.map(role => {
                    const policy = getNoticePolicy(role, selectedLeaveYear);
                    const example5yr = (safeNumericInputValue(policy.baseNotice) || 0) + 
                                       (safeNumericInputValue(policy.roleAdditionalNotice) || 0) + 
                                       (5 * (safeNumericInputValue(policy.tenureIncrementPerYear) || 0));
                    return (
                      <tr key={role}>
                        <td className="brio-font-medium">{role}</td>
                        <td className="text-center">
                          <input 
                            type="number" 
                            step="0.5"
                            value={safeNumericInputValue(policy.baseNotice)}
                            onChange={(e) => updateNoticePolicyMatrix(role, 'baseNotice', e.target.value, selectedLeaveYear)}
                            className="brio-modal-input"
                            style={{ width: '80px', textAlign: 'center', padding: '4px 8px' }}
                          />
                        </td>
                        <td className="text-center">
                          <input 
                            type="number" 
                            step="0.5"
                            value={safeNumericInputValue(policy.roleAdditionalNotice)}
                            onChange={(e) => updateNoticePolicyMatrix(role, 'roleAdditionalNotice', e.target.value, selectedLeaveYear)}
                            className="brio-modal-input"
                            style={{ width: '80px', textAlign: 'center', padding: '4px 8px' }}
                          />
                        </td>
                        <td className="text-center">
                          <input 
                            type="number" 
                            step="0.1"
                            value={safeNumericInputValue(policy.tenureIncrementPerYear)}
                            onChange={(e) => updateNoticePolicyMatrix(role, 'tenureIncrementPerYear', e.target.value, selectedLeaveYear)}
                            className="brio-modal-input"
                            style={{ width: '80px', textAlign: 'center', padding: '4px 8px' }}
                          />
                        </td>
                        <td className="text-center">
                          {(() => {
                            const raw = example5yr;
                            const rounded = Math.ceil(raw);
                            const capped = Math.min(rounded, 6);
                            return (
                              <span className="brio-font-medium">
                                {capped} months
                                {capped === 6 && raw > 6 && (
                                  <span className="text-xs ml-1" style={{ color: 'var(--brio-orange)' }}>(capped)</span>
                                )}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
